import { useRef, useState } from 'react'

import { GAMES, MEMORY_SIZES, type MemorySize } from '@hazel/shared'

import { Button } from '../components/Button.js'
import { Card } from '../components/Card.js'
import { useBarTitle } from '../layouts/bar-title.js'
import { Announce } from './Announce.js'
import { useRoundFocus } from './useRoundFocus.js'
import { useLeaveOnEscape } from './useLeaveOnEscape.js'
import { LeaveGame } from './LeaveGame.js'
import { KeyCap } from '../components/KeyCap.js'
import { KeyLegend } from '../components/KeyLegend.js'
import { LEAVE_SHORTCUT } from '../components/shortcuts.js'
import { Milestone } from './Milestone.js'
import { PlayAgain } from './PlayAgain.js'
import { RaceTrack } from './RaceTrack.js'
import { LANE, RACE_ART, racerPicture } from './raceArt.js'
import { MemoryCard } from './MemoryCard.js'
import { digitFrom, useGameKeys } from './useGameKeys.js'
import { columnsFor, MEMORY_LAYOUT, WIDE_FROM } from './memoryLayout.js'
import { useIsWide } from './useIsWide.js'
import { useMemory } from './useMemory.js'
import { useRound, type DealtRound } from './useRound.js'
import { TRACK_FOR_GAME } from '../sound/cues.js'
import { useCueOnChange, useCueOnEach, useRoundEndCues, useRoundMusic } from './gameSound.js'

/** Words for the board sizes, because "12 cards" is not what anybody is choosing. */
const SIZE_LABELS: Readonly<Record<MemorySize, string>> = {
  8: 'Easy',
  12: 'Steady',
  16: 'Tricky',
  20: 'Hard',
  24: 'Very hard',
  32: 'Enormous',
  // Twenty pairs of twenty-one teddies, so one sits out — a different one each
  // game. Twenty used to say "the whole collection", which was true of ten
  // teddies and quietly stopped being so when eleven more arrived.
  40: 'Nearly every teddy',
}

/**
 * How big a board, asked before there is one.
 *
 * It used to sit under the board and could be changed mid-game, which meant
 * the size control took room from the cards on every screen for a decision
 * made once — and changing it threw away the round in progress anyway.
 */
function ChooseSize({ onChoose }: { onChoose: (size: MemorySize) => void }) {
  const leaveOnEscape = useLeaveOnEscape()

  useGameKeys((event) => {
    // The prompt is a screen of its own, so it needs the same way out as every
    // other one. Getting stuck on the way in would be a worse trap than getting
    // stuck on the way out.
    if (event.key === 'Escape') return void leaveOnEscape(false)

    const digit = digitFrom(event)
    if (!digit) return

    // Numbered the way the picker's games are, so the same habit works twice.
    const chosen = MEMORY_SIZES[Number(digit) - 1]
    if (chosen !== undefined) onChoose(chosen)
  })

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-5 [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:gap-3">
      <Card className="flex w-full flex-col items-center gap-4">
        <h2 className="text-(length:--text-heading)">How big a board?</h2>

        {/*
          Tighter on the television, where seven of these are three rows more
          than there used to be. A board that runs past the screen is one thing
          — the frame carries it — but a list of choices that does is worse:
          the last option is the newest and the one she is most likely to be
          looking for.
        */}
        <div className="flex w-full flex-col gap-2 [[data-scale=tv]_&]:gap-1">
          {MEMORY_SIZES.map((option, index) => (
            <Button
              key={option}
              variant="secondary"
              block
              onClick={() => onChoose(option)}
              aria-label={`${SIZE_LABELS[option]}, ${option} cards. Press ${index + 1}`}
            >
              <span className="flex w-full items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <span aria-hidden="true" className="[@media(pointer:coarse)]:hidden">
                    <KeyCap>{index + 1}</KeyCap>
                  </span>
                  {SIZE_LABELS[option]}
                </span>
                <span className="text-ink-muted text-(length:--text-small)">
                  {`${option} cards`}
                </span>
              </span>
            </Button>
          ))}
        </div>
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={[
          { keys: ['1', '–', String(MEMORY_SIZES.length)], does: 'Pick a board' },
          LEAVE_SHORTCUT,
        ]}
      />
    </div>
  )
}

export function Memory() {
  // The bar carries the name while she is here, in place of "Hazel's
  // Games". One call rather than one per screen: the chooser and the
  // board are both this component, so the name does not blink out between
  // picking a size and being dealt one.
  useBarTitle(GAMES.memory.title)

  /*
    The screen's music, not the round's.

    On the round it started when play did — so Sky's was silent through "How
    big a board?", and every game had a beat of nothing while its round was
    being dealt. Asking here means it also survives another go, which is the
    same screen and should not restart the track.
  */
  useRoundMusic(TRACK_FOR_GAME.memory)

  // Nothing is dealt until she has said how big, so the size is settled before
  // a round exists rather than being changed underneath one.
  const [size, setSize] = useState<MemorySize | null>(null)

  if (size === null) return <ChooseSize onChoose={setSize} />

  return <MemoryRound size={size} />
}

function MemoryRound({ size }: { size: MemorySize }) {
  const { round, again, failed } = useRound<{ size: MemorySize }>({ game: 'memory', size })

  if (failed) {
    return (
      <p role="alert" className="text-coral py-16 text-center">
        We couldn’t start a game just now. Have another go in a moment.
      </p>
    )
  }

  if (!round) {
    return (
      <p role="status" className="py-16 text-center">
        Shuffling the cards…
      </p>
    )
  }

  // Keyed on the seed, so a new round deals a new board rather than leaving the
  // old cards on screen under a new heading.
  return <MemoryBoard key={round.seed} round={round} size={size} onAgain={again} />
}

function MemoryBoard({
  round,
  size,
  onAgain,
}: {
  round: DealtRound<{ size: MemorySize }>
  size: MemorySize
  onAgain: () => void
}) {
  const leaveOnEscape = useLeaveOnEscape()
  const game = useMemory(round, onAgain)
  const { view } = game
  const board = useRoundFocus<HTMLDivElement>(round.seed, view.status !== 'playing')

  /**
   * Where the keyboard is pointing.
   *
   * A grid needs a cursor when there is no mouse. Arrow keys move it and Enter
   * turns a card over — tabbing through twenty cards to reach the one you
   * remember would be its own memory game, and a worse one.
   */
  const [cursor, setCursor] = useState(0)

  /*
    A miss is a pair of flips that did not become a pair. Both are counted
    rather than watched, because two misses running are the same view twice and
    the second one still wants a sound.
  */
  useCueOnEach(view.flips, 'card.flip')
  useCueOnEach(view.pairsFound, 'pair.found')
  useCueOnEach(Math.floor(view.flips / 2) - view.pairsFound, 'pair.missed')
  useCueOnChange(cursor, 'move')
  useRoundEndCues(view.status !== 'playing', game.milestone)
  // Measured from the same table that draws the grid, so the cursor moves by
  // the number of columns actually on screen rather than by a guess.
  const wide = useIsWide(WIDE_FROM)
  const columns = columnsFor(size, wide)

  /** The card buttons, so the cursor can carry real focus with it. */
  const cards = useRef(new Map<number, HTMLButtonElement>())

  function step(by: number): void {
    const next = cursor + by
    // Stops at the edges rather than wrapping. Wrapping loses somebody who is
    // not looking at the cursor, which on a television is everybody.
    if (next < 0 || next >= view.cards.length) return

    setCursor(next)

    // Focus goes where the ring goes, every time.
    //
    // Without this the ring and the browser's own focus are two separate
    // things: a card focused by Tab or by an earlier tap keeps the keypress,
    // and Enter turns over a card nobody was pointing at. A card already found
    // is disabled and cannot take focus, so the board takes it instead — where
    // Enter still means the cursor.
    const target = cards.current.get(next)
    target?.focus()
    if (document.activeElement !== target) board.current?.focus()
  }

  useGameKeys((event) => {
    // Before the status gate: a finished board is exactly when somebody wants
    // out, and a board has nothing to clear at any point.
    if (event.key === 'Escape') return void leaveOnEscape(false)

    if (view.status !== 'playing') {
      if (event.key === 'Enter') game.again()
      return
    }

    switch (event.key) {
      case 'ArrowLeft':
        return step(-1)
      case 'ArrowRight':
        return step(1)
      case 'ArrowUp':
        return step(-columns)
      case 'ArrowDown':
        return step(columns)
      case 'Enter':
      case ' ':
        // Only reached while focus is on the board itself. With focus on the
        // cursor's card the browser presses it, which is the same card and the
        // same flip — see the note in step().
        event.preventDefault()
        if (!game.revealing) game.flip(cursor)
        return
      default:
    }
  })

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-4 [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1">
      <Card
        ref={board}
        tabIndex={-1}
        className="outline-none flex w-full flex-col items-center gap-4 [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1"
      >
        <p className="text-ink-muted text-(length:--text-small)">
          {`${view.pairsFound} of ${view.pairsTotal} pairs · ${view.flips} cards turned`}
        </p>

        {/* Pairs, not cards. Announcing every card as it turns would talk over
            somebody trying to remember where the last one was. */}
        <Announce>
          {[
            view.status === 'playing'
              ? `${view.pairsFound} of ${view.pairsTotal} pairs found`
              : `You found them all. ${view.pairsTotal} pairs, in ${view.flips} cards turned.`,
            game.milestone ? `That’s ${game.milestone} games played.` : '',
          ]
            .filter(Boolean)
            .join(' ')}
        </Announce>

        {/*
          Sky, swimming to a pearl.

          One step per pair found, and nobody racing her: the board is not
          against her, and inventing an opponent for a game of concentration
          would be inventing jeopardy that is not there.
        */}
        <RaceTrack
          goal={RACE_ART.pearl}
          racers={[
            {
              key: 'sky',
              name: 'Sky',
              picture: racerPicture('sky'),
              steps: view.pairsFound,
              of: view.pairsTotal,
              tint: LANE.sky,
            },
          ]}
        />

        {/*
          On the television the board is bounded by the height left over, not
          only by the width.

          A grid of square cards sized by width alone grows as tall as it
          likes: eight cards on a 1920×1080 screen wanted 1525px of page and
          twenty wanted 2078px, so most of the board sat below the fold — on a
          set with no mouse and nothing to scroll with.

          So on the television the rows divide the height and the cards, still
          square, take their size from a row. There is no arithmetic here to
          get wrong and nothing measured at runtime: the board cannot be taller
          than the box it was handed.
        */}
        <div className="contents [[data-scale=tv]_&]:flex [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:w-full [[data-scale=tv]_&]:flex-1 [[data-scale=tv]_&]:flex-col [[data-scale=tv]_&]:items-center [[data-scale=tv]_&]:justify-center">
          <div
            className={`grid w-full gap-2 sm:gap-3 ${MEMORY_LAYOUT[size].className} [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:w-auto [[data-scale=tv]_&]:max-w-full [[data-scale=tv]_&]:flex-1 [[data-scale=tv]_&]:gap-0 ${MEMORY_LAYOUT[size].tvClassName}`}
          >
            {view.cards.map((card) => (
              <MemoryCard
                key={card.card}
                position={card.card}
                teddy={card.teddy}
                matched={card.matched}
                cursor={card.card === cursor}
                ref={(element) => {
                  if (element) cards.current.set(card.card, element)
                  else cards.current.delete(card.card)
                }}
                // While a missed pair is showing, taps are refused by the engine
                // anyway. Disabling them here means a child gets a card that does
                // not respond rather than one that silently does nothing.
                disabled={game.revealing || view.status !== 'playing'}
                /*
                 * The cursor follows the finger, and takes real focus with it.
                 *
                 * Without this the ring stayed where the keyboard left it while
                 * a different card turned over, so the next arrow key stepped
                 * from somewhere nobody had touched — the same "two things
                 * pointing at different cards" this board's cursor was built to
                 * stop, surviving on the path nobody tested.
                 *
                 * Focus is taken explicitly because Safari does not give it to a
                 * button on click. jsdom focuses on click the way Chromium does,
                 * so the suite cannot tell the two apart and that half is
                 * uncovered — DOL-46.
                 *
                 * The ring moves whether or not the engine accepts the flip, and
                 * that is safe only because `disabled` above already covers every
                 * reason it could refuse: a finished board, a pair still
                 * showing, a card already turned. `flip` returns nothing, so
                 * there is no way to gate on acceptance even if we wanted to —
                 * which means a new reject reason without a matching change to
                 * `disabled` would put the ring back on a card that never
                 * turned. That coupling is the thing to keep in view.
                 */
                onFlip={(element) => {
                  element?.focus()
                  setCursor(card.card)
                  game.flip(card.card)
                }}
              />
            ))}
          </div>
        </div>

        {view.status !== 'playing' ? (
          <div className="flex flex-col items-center gap-3">
            <p className="font-display text-(length:--text-title)">You found them all!</p>
            <p className="text-ink-muted [[data-scale=tv]_&]:text-(length:--text-heading)">
              {`${view.flips} cards turned over. ${
                view.flips <= view.pairsTotal * 2 ? 'Perfect!' : 'See if you can do it in fewer.'
              }`}
            </p>
            {game.milestone ? <Milestone games={game.milestone} /> : null}

            <PlayAgain label="Play again" does="to play again" onAgain={game.again} />
          </div>
        ) : null}
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={
          view.status === 'playing'
            ? [
                { keys: ['←', '↑', '↓', '→'], does: 'Move around the board' },
                { keys: ['Enter'], does: 'Turn a card over' },
                LEAVE_SHORTCUT,
              ]
            : [{ keys: ['Enter'], does: 'Play again' }, LEAVE_SHORTCUT]
        }
      />
    </div>
  )
}
